import { useSelector } from 'react-redux';
import { ActionType, TextKey } from '@mews-commander/enums';
import { ModalBodyWrapper, ModalWrapper } from '@mews-commander/ui';
import { IconName } from '@optimus-web/core';
import { LocalizedText } from '@mews/framework';
import { compactArray } from '@mews/fp';
import { ApplicationState, CommanderThunkDispatch, WithEnterpriseId, WithGuideLink } from '@mews-commander/types';
import { useResolveRoute } from '@mews/routing';
import { fetchRates } from '@mews-commander/legacy-entities/src/ducks/exchangeRates';
import { getEnterpriseName } from '@mews-commander/legacy-entities/src/ducks/enterprises';
import { getEnterpriseDetailUrl } from '@mews-commander/legacy-entities/src/selectors/enterprises';
import ModalHeader from '../../legacy/React/Components/Common/ModalHeader';
import { Breadcrumbs } from '../../components/BreadcrumbList';
import { HeadingAction } from '../../legacy/React/Components/HeadingAction/Action/types';
import { setExchangeRates } from './actions';
import Exchange from './components/Exchange';
import { getAvailableNonExchangeCurrencies, getCreateExchangeRateUrl } from './reducers';

export const fetchExchangeRateDependencies = async (
    dispatch: CommanderThunkDispatch,
    { enterpriseId }: WithEnterpriseId
) => {
    await dispatch(fetchRates({ enterpriseId })).then(() => dispatch(setExchangeRates(enterpriseId)));
};

export const ExchangeRateContainer: React.FC<WithEnterpriseId & WithGuideLink> = ({ enterpriseId, guideLink }) => {
    const resolveRoute = useResolveRoute();
    const name = useSelector((state: ApplicationState) => getEnterpriseName(state, { enterpriseId }));
    const breadcrumbUrl = useSelector((state) => getEnterpriseDetailUrl(state, { enterpriseId, resolveRoute }));
    const createRateUrl = useSelector((state) => getCreateExchangeRateUrl(state, { enterpriseId, resolveRoute }));

    const availableCurrencies = useSelector((state) =>
        getAvailableNonExchangeCurrencies(state, { scope: enterpriseId })
    );

    const breadcrumbs: Breadcrumbs = [
        {
            text: name,
            url: breadcrumbUrl,
        },
        {
            text: <LocalizedText textKey={TextKey.CurrencyAcceptancePlural} />,
            active: true,
        },
    ];

    const actions: HeadingAction[] = compactArray([
        availableCurrencies.length !== 0
            ? {
                  url: createRateUrl,
                  icon: IconName.Add,
                  text: <LocalizedText textKey={TextKey.CurrencyAcceptance} />,
                  type: ActionType.Link,
              }
            : null,
        {
            url: guideLink,
            target: '_blank',
            icon: IconName.Help,
            text: <LocalizedText textKey={TextKey.Help} />,
            type: ActionType.Link,
        },
    ]);

    return (
        <ModalWrapper>
            <ModalHeader
                title={<LocalizedText textKey={TextKey.CurrencyAcceptancePlural} />}
                breadcrumbs={breadcrumbs}
                actions={actions}
            />
            <ModalBodyWrapper>
                <Exchange scope={enterpriseId} />
            </ModalBodyWrapper>
        </ModalWrapper>
    );
};
